import { FlexCenter } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import React, { useCallback, useRef } from "react";
import translate from "src/lib/translate";
import { toast } from "src/state/state";
import CustomQuestionnaire from "src/ui/components/CustomQuestionnaire/CustomQuestionnaire";
import type {
  SignupCustomStep,
  SignupCustomStepQuestionnaire as SignupCustomStepQuestionnaireType
} from "src/ui/components/SignupCustomContent/SignupCustom.types";
import { SignupCustomStepType } from "src/ui/components/SignupCustomContent/SignupCustom.types";
import SignupCustomBloc from "../state/SignupCustomBloc";

const SignupCustomStepQuestionnaireContent: FC<{
  step: SignupCustomStepQuestionnaireType;
  onStepCompleted: (
    step: SignupCustomStepQuestionnaireType
  ) => Promise<boolean> | boolean;
}> = ({ step, onStepCompleted }) => {
  const [{ stepForced }, { showSaving, hideSaving }] =
    useBlocNext(SignupCustomBloc);
  const [showQuestionnaire, setShowQuestionnaire] = React.useState(true);
  const completed = useRef({ lastStep: false, dataSent: false });

  const handleSubmit = () => {
    if (completed.current.lastStep && completed.current.dataSent) {
      setShowQuestionnaire(false);
      void onStepCompleted(step);
    }
  };

  const handleDataSent = () => {
    completed.current.dataSent = true;
    handleSubmit();
  };
  const handleLastStepCompleted = () => {
    completed.current.lastStep = true;
    handleSubmit();
  };

  const handleSavingChange = useCallback(
    (isSaving: boolean, error?: boolean) => {
      if (isSaving) {
        showSaving({
          text: translate("savingIndicator.info", { context: "questionnaire" })
        });
      } else {
        if (error) {
          hideSaving();
          toast.error("error.savingQuestionnaire");
        }
      }
    },
    [showSaving, hideSaving]
  );

  return (
    <>
      <FlexCenter
        style={{
          opacity: showQuestionnaire ? 1 : 0
        }}
      >
        <CustomQuestionnaire
          useRouting
          forceRepeat={stepForced}
          onDataSent={handleDataSent}
          onLastStepCompleted={handleLastStepCompleted}
          onSavingChange={handleSavingChange}
          hideSavingUI
          id={step.questionnaireId}
          resetScrollOnEachStep
          answerOverrides={step.answerOverrides}
          filterAnswerOptions={step.filterAnswerOptions}
        />
      </FlexCenter>
    </>
  );
};

const SignupCustomStepQuestionnaire: FC<{
  step: SignupCustomStep;
  onStepCompleted: (step: SignupCustomStep) => Promise<boolean> | boolean;
}> = React.memo(({ step, onStepCompleted }) => {
  const isQuestionnaire = step.type === SignupCustomStepType.Questionnaire;
  return isQuestionnaire ? (
    <SignupCustomStepQuestionnaireContent
      step={step}
      onStepCompleted={onStepCompleted}
    />
  ) : (
    <></>
  );
});

export default SignupCustomStepQuestionnaire;
